/**
 * Small helpers used by the WASM wrappers.
 *
 * this package is a leaf — it deliberately does not import from `@mdatools-apps/shared`,
 * so the two packages stay in a one-way dependency relationship. `seq` is duplicated
 * from `shared/utils.js` on purpose; it is trivial and stable.
 */


/**
 * Creates array with a sequence of values from `start` to `end`.
 * @param {number} start first value of sequence.
 * @param {number} end last value of sequence.
 * @param {number} by increment.
 * @returns {Array} array with sequence.
 */
export function seq(start, end, by = 1) {
   // the count of steps of 'by' which fit between the two ends, plus the start
   const length = Math.floor((end - start) / by) + 1;
   const out = Array(length);
   for (let i = 0; i < length; i++) {
      out[i] = start + i * by;
   }
   return out;
}


// largest byte count a single allocation may ask for. this is not the point where the size wraps -
// that is 2**32, because the JS to wasm boundary applies ToInt32 and 'size_t' is unsigned - it is a
// conservative signed-linear-memory policy. the largest block this build actually serves is 1.5 GiB,
// so the ceiling only makes a too-large request fail with the same message every time instead of
// depending on how fragmented the heap happens to be. measured: 1536 MB is served, 1792 MB is not
const MAX_ALLOC_BYTES = 0x7fffffff;


/**
 * Allocates a block on the WASM heap, refusing invalid and unsupported sizes.
 *
 * @param {Object} Module - emscripten module.
 * @param {number} nbytes - number of bytes to allocate.
 * @param {string} what - name of the buffer, used in error messages.
 * @returns {number} pointer to the allocated block.
 */
export function alloc(Module, nbytes, what) {

   // '_malloc' takes an i32, so a NaN or a non-integer size is coerced rather than rejected and
   // comes back as a valid pointer to a block of the wrong length - the returned pointer can not
   // be used to tell that apart, which is why the size is checked before the call and not after
   if (!Number.isSafeInteger(nbytes) || nbytes <= 0) {
      throw new Error('alloc: invalid size for ' + what + ' (' + nbytes + ' bytes).');
   }

   if (nbytes > MAX_ALLOC_BYTES) {
      throw new Error('alloc: ' + what + ' needs ' + nbytes + ' bytes, more than the WASM heap can hold.');
   }

   // this build uses mimalloc, which has no abort-on-failure path, so a failed allocation is a 0
   // the caller must not use. writing to it would not trap either - offset 0 is ordinary memory
   const ptr = Module._malloc(nbytes);
   if (ptr === 0) {
      throw new Error('out of memory: could not allocate ' + nbytes + ' bytes for ' + what + '.');
   }

   return ptr;
}


/**
 * Allocates a block on the WASM heap and copies an array into it.
 *
 * @param {Object} Module - emscripten module.
 * @param {string} view - name of the heap view to copy through ('HEAPF64' or 'HEAP32').
 * @param {TypedArray} arr - values to copy.
 * @param {string} what - name of the buffer, used in error messages.
 * @returns {number} pointer to the allocated block.
 */
export function allocCopy(Module, view, arr, what) {

   // 'ArrayBuffer.isView' is true for a DataView as well, which has no 'length' and no
   // 'BYTES_PER_ELEMENT', so it is ruled out here rather than left to fail as a NaN size
   if (!ArrayBuffer.isView(arr) || arr instanceof DataView) {
      throw new Error('allocCopy: ' + what + ' must be a typed array.');
   }

   // the element width comes from the heap view rather than from 'arr', so a caller which hands
   // over an array of the wrong kind can not make the allocation and the copy disagree. sizing
   // with 'arr.BYTES_PER_ELEMENT' and then dividing the offset by the same value is what let a
   // Float32Array get a 4-bytes-per-element block and then be written into 'HEAPF64' starting at
   // element 'ptr / 4' - byte address '2 * ptr', a different block altogether
   const bytesPerElement = Module[view].BYTES_PER_ELEMENT;
   const ptr = alloc(Module, arr.length * bytesPerElement, what);

   try {
      // the view is read again here on purpose: '_malloc' can grow the heap, and growing replaces
      // 'Module.HEAPF64' and 'Module.HEAP32' with views onto the new buffer
      Module[view].set(arr, ptr / bytesPerElement);
   } catch (e) {
      // the block is already allocated but the pointer has not been returned yet, so the caller's
      // 'finally' can not free it - this function owns it until it hands it over. a BigInt array
      // reaches here: it passes the check above and then makes '.set' throw
      Module._free(ptr);
      throw e;
   }

   return ptr;
}


/**
 * Returns a dense copy of an index array, refusing it unless every entry is a whole number.
 *
 * The copy is what makes the check work at all: '.every' SKIPS holes, so a sparse '[5, ,]' passes
 * a check written against the original array, and the hole then reaches C as index 0 because
 * 'Int32Array' turns 'undefined' into 0. 'Array.from' fills holes with 'undefined' instead, which
 * 'Number.isInteger' refuses. Removing the 'Array.from' makes this function ACCEPT a sparse list,
 * which turns red every sparse row of 'test-index-guards' that routes through here - that is what
 * pins the line. One sparse row stays green under that mutation and is meant to: 'gls_predict'
 * checks its own outlier list inline rather than calling this, so nothing about it changes.
 *
 * It does NOT test the container: 'Array.from' erases that, turning a Set or an array-like into an
 * ordinary array. Callers must refuse non-arrays BEFORE calling this - 'isArrCorrect' does that,
 * and the callers which have no range check of their own use 'Array.isArray' first.
 *
 * Callers give the result its own name rather than assigning it back over the argument: a fit
 * model and a predict result hand the caller's own index array back, and 'test-pca-wasm-exclvars'
 * pins that by identity.
 *
 * @param {Array} arr - array of indices to check.
 * @param {string} message - the error to raise when an entry is not a whole number.
 * @returns {Array} a dense copy of the values.
 */
export function checkedIndexCopy(arr, message) {
   const copy = Array.from(arr);
   if (!copy.every(Number.isInteger)) {
      throw new Error(message);
   }
   return copy;
}


/**
 * Checks if array is correct and all values are within given range.
 * @param {Array} arr - array.
 * @param {number} minVal - smallest expected value.
 * @param {number} maxVal - largest expected value.
 * @returns {boolean} result of check.
 */
export function isArrCorrect(arr, minVal, maxVal) {
   if (!Array.isArray(arr)) return false;
   if (arr.length === 0) return true;

   for (let i = 0; i < arr.length; i++) {
      const v = arr[i];
      if (v < minVal || v > maxVal) return false;
   };

   return true;
}


/**
 * Checks that a flat model buffer is self-consistent before it is handed to wasm.
 *
 * the C side reads the model dimensions from the first values of 'm.v' and derives every offset
 * inside the buffer from them - it never sees how long 'm.v' actually is. an inflated header
 * therefore produces valid looking offsets past the end of the array, and on wasm32 reading or
 * writing there does not trap. this function is the only place where the two are compared.
 *
 * the check has to be header derived. computing the expected length from the JSON metadata
 * ('m.ncomp' and friends) instead looks equivalent and is not: the metadata is a separate copy
 * which a forged file need not touch, so it agrees with itself while the header lies. see
 * 'docs/wasm/model-buffer-validation.md' for the measured case.
 *
 * only the DIMENSIONAL prefix of the header is validated here. the GLS header continues with
 * 'alpha', 'lambda' and 'method', and none of those is a positive integer - a default 'lambda' is
 * 0 and a non default one is a power of ten, usually fractional. validating them as dimensions
 * would reject honest models.
 *
 * @param {Object} m - the model object.
 * @param {string} what - name of the calling wrapper, used in the error messages.
 * @param {Array} dims - the dimensional prefix, one '[index in m.v, metadata property]' per field.
 * @param {Function} getLength - receives the validated header values in header order and returns
 *        the expected buffer length. the JS length functions do not take their arguments in header
 *        order, so the caller reorders them here rather than leaving it implicit.
 */
export function checkModelBuffer(m, what, dims, getLength) {

   // the element type reaches us from the model file: the JSON reviver builds the array from a
   // '__type' string it finds there. 'gls_predict' still sizes its block with
   // 'm.v.BYTES_PER_ELEMENT' and then writes through 'HEAPF64', so a Float32Array would get a
   // block of half the bytes it needs and land at twice its own pointer. 'allocCopy' protects the
   // other wrappers from that, but a model is checked here in either case: quietly widening a
   // Float32Array would hide a wrong model rather than report it
   //
   // 'instanceof' is realm bound, so a genuine Float64Array built in another realm would be
   // refused here. that is unreachable in this project rather than merely unlikely: nothing in
   // any app creates a Worker, an iframe or a postMessage channel, so a model is only ever
   // produced by a '*_fit' in this realm or revived by 'JSON.parse' in this realm
   // the width is checked separately because 'instanceof' does not pin it: a class extending
   // Float64Array can override 'BYTES_PER_ELEMENT' and still pass the test above. every genuine
   // Float64Array reports 8, so this refuses nothing real
   if (!(m.v instanceof Float64Array) || m.v.BYTES_PER_ELEMENT !== 8) {
      throw new Error(what + ': model values "v" must be a Float64Array.');
   }

   const header = [];
   for (let i = 0; i < dims.length; i++) {
      const [ind, prop] = dims[i];
      const value = m.v[ind];

      // C converts the header with '(int)round()', which accepts anything within half a unit of
      // an integer - this is deliberately stricter than that, and no fitted model has ever had a
      // fractional dimension
      if (!Number.isSafeInteger(value) || value < 1) {
         throw new Error(
            what + ': model header value "' + prop + '" (' + value + ') must be a positive integer.'
         );
      }

      // the header and the metadata are two independent copies of the same numbers, and the
      // result buffer is sized from the metadata while C lays the result out from the header.
      // this comparison is what keeps them from drifting apart. it also makes a separate type
      // check on the metadata unnecessary - a missing or string valued property can not be
      // strictly equal to a number
      if (value !== m[prop]) {
         throw new Error(
            what + ': model header value "' + prop + '" (' + value + ') does not match the model ' +
            'property (' + m[prop] + ') - the model is corrupt or uses an unsupported layout.'
         );
      }

      header.push(value);
   }

   // exact equality, not 'at least'. a longer buffer is not a safely compatible one: the wasi
   // model format shares the whole dimensional prefix with the wasm format and differs only by
   // two extra header values, so it is always longer and would pass a '>=' test while every
   // offset after the header sits two values off. every fit allocates exactly this length and
   // slices exactly it back, so an honest model matches on the nose
   const expected = getLength(...header);
   if (m.v.length !== expected) {
      throw new Error(
         what + ': model values "v" have wrong length (' + m.v.length + ' instead of ' + expected +
         ') - the model is corrupt or uses an unsupported layout.'
      );
   }
}


/**
 * Wraps a module factory so that the lines the C code prints to stderr are kept on the module
 * it builds, in 'Module.errlines', for 'wasmError' to put into the error a wrapper throws. The
 * lines reach the console as before.
 * @param {Function} factory - a module factory (the default export of a '*_wasm.js' glue file).
 * @returns {Function} a factory taking no arguments.
 */
export function withErrorLines(factory) {
   return () => {
      const arg = { errlines: [] };
      arg.printErr = (line) => { arg.errlines.push(line); console.error(line); };
      return factory(arg);
   };
}


/**
 * Makes the error a wrapper throws when a C function returns its failure code: the lines the C
 * code printed during the call when the module keeps them (see 'withErrorLines'), the generic
 * text otherwise. Empty lines (a C print starting with a newline) are left out.
 * @param {Object} Module - the module the call ran on.
 * @param {string} fname - the name the error message starts with.
 * @param {number} mark - length of 'Module.errlines' before the call.
 * @returns {Error} the error to throw.
 */
export function wasmError(Module, fname, mark) {
   const lines = Module.errlines ? Module.errlines.slice(mark).filter(l => l !== '') : [];
   return new Error(fname + ': ' + (lines.length > 0 ? lines.join(' ') : 'unexpected error.'));
}
